import React, { useState, useRef, useEffect } from "react";
import { FaCheck, FaCreditCard, FaPaypal, FaChevronDown, FaCrown, FaStar, FaShieldAlt, FaBolt } from "react-icons/fa";
import AnimatedModal from "./AnimatedModal";
import { subscriptionAPI, paymentAPI, api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { FaGift } from "react-icons/fa";

function StripePaymentForm({ clientSecret, purchaseId, paymentIntentId, onSuccess, onError, onPaymentSuccess, onStripeReady }) {
  const { setUser } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Notify parent when Stripe Elements is ready
  useEffect(() => {
    if (elements && typeof onStripeReady === 'function') {
      onStripeReady();
    }
  }, [elements, onStripeReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!stripe || !elements) return;
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {},
      redirect: "if_required",
    });
    if (stripeError) {
      setLoading(false);
      setError(stripeError.message);
      onError && onError(stripeError);
      return;
    }
    // Call confirm API after successful payment
    try {
      const confirmRes = await paymentAPI.confirmSubscriptionPurchase({
        paymentIntentId: paymentIntentId || paymentIntent?.id,
        purchaseId,
      });
      // Refresh token and user info after payment confirmation
      try {
        const subRes = await api.getUserSubscriptions();
        console.log('user-subscriptions response after payment:', subRes);
        if (subRes && subRes.activeSubscription) {
          setUser((prev) => ({
            ...prev,
            subscriptionStatus: subRes.activeSubscription.name === 'Trial' ? 'trial' : 'active',
            subscriptionPlan: subRes.activeSubscription.name,
            activeSubscription: subRes.activeSubscription,
          }));
        }
      } catch (refreshErr) {
        // Ignore refresh error, but log it
        console.error('Failed to update subscription after payment:', refreshErr);
      }
      setLoading(false);
      if (confirmRes && confirmRes.message === "Payment confirmed and purchase activated") {
        onSuccess && onSuccess();
        if (onPaymentSuccess) onPaymentSuccess();
      } else {
        setError("Payment succeeded but confirmation failed. Please contact support.");
        onError && onError(new Error("Confirmation failed"));
      }
    } catch (err) {
      setLoading(false);
      setError("Payment succeeded but confirmation failed. Please contact support.");
      onError && onError(err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4">
      <PaymentElement />
      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
      <button
        type="submit"
        disabled={loading || !stripe || !elements}
        className="w-full btn-primary mt-6"
      >
        {loading ? "Processing..." : "Pay Now"}
      </button>
    </form>
  );
}

export default function PaymentScreen({ isOpen, onClose, onPaymentSuccess }) {
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollContainerRef = useRef(null);
  const [groupedPlans, setGroupedPlans] = useState([]);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [plans, setPlans] = useState(null);
  const [features, setFeatures] = useState([
    "Upload unlimited engineering PDFs (AS3000, standards, codes, manuals)",
    "Advanced metadata extraction (clauses, headings, rules, definitions)",
    "Natural language queries for technical documents",
    "Multi-tenant workspace for engineering teams",
    "Custom AI models trained on engineering standards",
    "Context-aware answers from document metadata",
    "Team collaboration and document sharing",
    "Export technical discussions and findings",
    "Advanced search across engineering standards",
    "Priority support for engineering workflows",
    "API access for engineering tool integrations",
    "Compliance tracking and audit trails",
    "Custom document categorization and tagging",
    "Real-time collaboration on technical queries"
  ]);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [showStripe, setShowStripe] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribeError, setSubscribeError] = useState("");
  const [allPackages, setAllPackages] = useState([]);
  const [allPaymentOptions, setAllPaymentOptions] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [stripePurchaseId, setStripePurchaseId] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState("");
  const [redeemSuccess, setRedeemSuccess] = useState("");

  // For testing, use the provided Stripe test key directly
  const stripePromise = loadStripe('pk_test_51RnM572K7fm6pByJxaJSVkY6EEiqvQQKd77o3LzrMgtm2zc9umjkDUIXINfECtIzNBD3Re2ks5dqTsC6HeUKDoiV00uvY8AABT');

  // Loading overlay for payment intent creation
  const showPaymentLoading = submitting && !showStripe;

  // Check if content is scrollable and handle scroll indicator
  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current;
        const isScrollable = scrollHeight > clientHeight;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setShowScrollIndicator(isScrollable && !isAtBottom);
      }
    };

    checkScrollable();
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollable);
      window.addEventListener('resize', checkScrollable);
      return () => {
        scrollContainer.removeEventListener('scroll', checkScrollable);
        window.removeEventListener('resize', checkScrollable);
      };
    }
  }, []);

  useEffect(() => {
    async function fetchPlansAndProviders() {
      try {
        const [plansData, providersData, paymentOptionsData] = await Promise.all([
          subscriptionAPI.getPackages(),
          paymentAPI.getProviders(),
          paymentAPI.getPaymentOptions()
        ]);
        setAllPackages(plansData); // Store the original packages
        setAllPaymentOptions(paymentOptionsData);
        // Group by name, merge durations
        const grouped = [];
        plansData.forEach(pkg => {
          let existing = grouped.find(g => g.name === pkg.name);
          if (!existing) {
            grouped.push({ ...pkg, durations: [...pkg.durations] });
          } else {
            existing.durations = [...existing.durations, ...pkg.durations];
          }
        });
        // Only keep plans with at least one duration
        const filtered = grouped.filter(plan => plan.durations && plan.durations.length > 0);
        setGroupedPlans(filtered);
        if (filtered.length > 0) {
          setCurrentPlanIndex(0);
          const plan = filtered[0];
          const monthly = plan.durations.find(d => d.type === "monthly");
          const yearly = plan.durations.find(d => d.type === "yearly");
          setPlans({
            monthly: monthly && {
              id: "monthly",
              name: `${plan.name} Monthly`,
              price: monthly.price,
              period: "month",
              description: plan.description,
              savings: null
            },
            yearly: yearly && {
              id: "yearly",
              name: `${plan.name} Annual`,
              price: yearly.price,
              period: "year",
              description: plan.description,
              savings: yearly.displayPrice && monthly ? `Save $${(monthly.price * 12 - yearly.price).toFixed(0)} (${Math.round(100 - (yearly.price / (monthly.price * 12)) * 100)}% off)` : null
            }
          });
          setFeatures(plan.features);
        }
        setPaymentProviders(providersData.filter(p => p.is_active));
        if (providersData.length > 0) {
          setPaymentMethod(providersData[0].name.toLowerCase());
        }
      } catch (err) {
        // fallback to static
        setPlans({
          monthly: {
            id: "monthly",
            name: "Engineering Pro Monthly",
            price: 29,
            period: "month",
            description: "Perfect for small engineering teams",
            savings: null
          },
          yearly: {
            id: "yearly",
            name: "Engineering Pro Annual",
            price: 290,
            period: "year",
            description: "Best value for engineering organizations",
            savings: "Save $58 (17% off)"
          }
        });
        setPaymentProviders([]);
      }
    }
    fetchPlansAndProviders();
  }, []);

  // Handle plan navigation
  const handlePrevPlan = () => {
    if (groupedPlans.length === 0) return;
    const newIndex = (currentPlanIndex - 1 + groupedPlans.length) % groupedPlans.length;
    setCurrentPlanIndex(newIndex);
    const plan = groupedPlans[newIndex];
    const monthly = plan.durations.find(d => d.type === "monthly");
    const yearly = plan.durations.find(d => d.type === "yearly");
    setPlans({
      monthly: monthly && {
        id: "monthly",
        name: `${plan.name} Monthly`,
        price: monthly.price,
        period: "month",
        description: plan.description,
        savings: null
      },
      yearly: yearly && {
        id: "yearly",
        name: `${plan.name} Annual`,
        price: yearly.price,
        period: "year",
        description: plan.description,
        savings: yearly.displayPrice && monthly ? `Save $${(monthly.price * 12 - yearly.price).toFixed(0)} (${Math.round(100 - (yearly.price / (monthly.price * 12)) * 100)}% off)` : null
      }
    });
    setFeatures(plan.features);
  };
  const handleNextPlan = () => {
    if (groupedPlans.length === 0) return;
    const newIndex = (currentPlanIndex + 1) % groupedPlans.length;
    setCurrentPlanIndex(newIndex);
    const plan = groupedPlans[newIndex];
    const monthly = plan.durations.find(d => d.type === "monthly");
    const yearly = plan.durations.find(d => d.type === "yearly");
    setPlans({
      monthly: monthly && {
        id: "monthly",
        name: `${plan.name} Monthly`,
        price: monthly.price,
        period: "month",
        description: plan.description,
        savings: null
      },
      yearly: yearly && {
        id: "yearly",
        name: `${plan.name} Annual`,
        price: yearly.price,
        period: "year",
        description: plan.description,
        savings: yearly.displayPrice && monthly ? `Save $${(monthly.price * 12 - yearly.price).toFixed(0)} (${Math.round(100 - (yearly.price / (monthly.price * 12)) * 100)}% off)` : null
      }
    });
    setFeatures(plan.features);
  };

  const handleSubscribe = async () => {
    setSubmitting(true);
    setSubscribeError("");
    try {
      // Find selected plan and payment provider
      const planObj = groupedPlans[currentPlanIndex];
      const duration = plans[selectedPlan];
      const provider = paymentProviders.find(p => p.name.toLowerCase() === paymentMethod);
      if (!planObj || !duration || !provider) throw new Error("Missing plan or payment provider");
      // Find the correct duration object for paymentOptionIds
      const durationObj = planObj.durations.find(d => d.type === selectedPlan);
      // Find the original package for this duration
      const originalPackage = allPackages.find(pkg =>
        pkg.durations.some(d => d._id === durationObj._id)
      );
      if (!originalPackage) throw new Error("Could not find original package for selected duration");
      // Find the correct payment option for the selected provider and currency
      let paymentOptionId = null;
      let paymentOptionObj = null;
      let currency = selectedCurrency;
      // Try to find a payment option for the selected provider and currency
      for (const id of durationObj.paymentOptionIds || []) {
        const opt = allPaymentOptions.find(o => o._id === id && o.provider.toLowerCase() === provider.name.toLowerCase() && (currency ? o.currency === currency : true));
        if (opt) {
          paymentOptionId = opt._id;
          paymentOptionObj = opt;
          if (!currency) currency = opt.currency;
          break;
        }
      }
      // If not found, fallback to first available
      if (!paymentOptionObj && (durationObj.paymentOptionIds || []).length > 0) {
        paymentOptionObj = allPaymentOptions.find(o => o._id === durationObj.paymentOptionIds[0]);
        paymentOptionId = paymentOptionObj?._id;
        if (paymentOptionObj && !currency) currency = paymentOptionObj.currency;
      }
      if (!paymentOptionObj) throw new Error("No valid payment option found for this plan/provider/currency");
      // Map userLimits to userlimit for backend compatibility
      const limits = {
        ...originalPackage.limits,
        userlimit: originalPackage.limits.userLimits,
      };
      delete limits.userLimits;
      // Build payload
      const payload = {
        subscription: {
          id: originalPackage._id, // Use the parent package id
          name: planObj.name,
          price: paymentOptionObj.price,
          limits,
        },
        paymentType: {
          id: paymentOptionObj._id,
          price: paymentOptionObj.price,
          paymentTypeName: paymentOptionObj.provider.toLowerCase(),
        },
      };
      const res = await paymentAPI.purchaseSubscription(payload);
      // Factory pattern for payment provider
      if (provider.name.toLowerCase() === "stripe") {
        setStripeClientSecret(res.clientSecret);
        setStripePurchaseId(res.id); // Use the top-level id
        setStripePaymentIntentId(res.paymentIntentId);
        setShowStripe(true);
      } else {
        // Handle other providers here
        alert("Payment provider not implemented yet.");
      }
    } catch (err) {
      setSubscribeError(err.message || "Failed to subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedeem = async () => {
    setRedeeming(true);
    setRedeemError("");
    setRedeemSuccess("");
    try {
      if (!redeemCode || redeemCode.trim().length < 4) throw new Error("Enter a valid code");
      const res = await paymentAPI.redeemSubscriptionCode(redeemCode.trim());
      setRedeemSuccess("Code applied! Your subscription has been activated.");
      if (typeof onPaymentSuccess === 'function') onPaymentSuccess();
      // Optionally close after a short delay
      setTimeout(() => onClose && onClose(), 1200);
    } catch (err) {
      setRedeemError(err.message || "Failed to apply code. Try again.");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-4xl">
      <div className="card-primary max-h-[90vh] relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-indigo-500/15 to-purple-500/15 blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-tr from-blue-500/10 to-cyan-500/10 blur-3xl"></div>
        </div>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 btn-icon"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Scrollable content */}
        <div 
          ref={scrollContainerRef}
          className="overflow-y-scroll max-h-[90vh] payment-scroll-container"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style dangerouslySetInnerHTML={{
            __html: `
              .payment-scroll-container::-webkit-scrollbar {
                display: none;
              }
            `
          }} />

          <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Upgrade to {groupedPlans.length > 0 ? groupedPlans[currentPlanIndex].name : "Engineering Pro"}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mt-2">
              Professional features, cleaner workflow, and priority support.
            </p>
            <div className="mt-5 inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
              <button
                type="button"
                onClick={() => setSelectedPlan('monthly')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium ${selectedPlan === 'monthly' ? 'bg-indigo-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:text-indigo-600'}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setSelectedPlan('yearly')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium ${selectedPlan === 'yearly' ? 'bg-indigo-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:text-indigo-600'}`}
              >
                Annual {plans?.yearly?.savings && <span className="ml-1 text-[11px] text-emerald-600 dark:text-emerald-400">({plans.yearly.savings})</span>}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - Plans */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Choose your plan</h3>

              {/* Plan Navigation Arrows */}
              {groupedPlans.length > 1 && (
                <div className="flex justify-between items-center mb-4">
                  <button onClick={handlePrevPlan} className="text-2xl px-3 py-1 rounded-full btn-icon">&#8592;</button>
                  <span className="text-gray-500 text-sm">{currentPlanIndex + 1} / {groupedPlans.length}</span>
                                      <button onClick={handleNextPlan} className="text-2xl px-3 py-1 rounded-full btn-icon">&#8594;</button>
                </div>
              )}

              {/* Plan Selection */}
              <div className="space-y-4">
                {plans && Object.values(plans).filter(Boolean).map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`p-5 border rounded-xl cursor-pointer transition-colors ${
                      selectedPlan === plan.id
                        ? 'border-indigo-600 ring-1 ring-indigo-100 dark:ring-indigo-900/40'
                        : 'border-gray-200 dark:border-gray-600 hover:border-indigo-300'
                    }`}
                  >
                    {plan.id === 'yearly' && plans?.monthly && (
                      <div className="mb-2 inline-flex items-center text-[11px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded">Best value</div>
                    )}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 ${
                              selectedPlan === plan.id
                                ? "border-indigo-500 bg-indigo-500"
                                : "border-gray-300"
                            }`}
                          >
                            {selectedPlan === plan.id && (
                              <div className="w-full h-full rounded-full bg-white scale-50"></div>
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-800 dark:text-gray-100">
                            {plan.name}
                          </h4>
                          {plan.savings && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              {plan.savings}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 ml-7">
                          {plan.description}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          ${plan.price}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          per {plan.period}
                        </div>
                        {plan.id === "yearly" && plans.monthly && plans.yearly && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            ${plans.monthly ? (plans.yearly.price / 12).toFixed(0) : plan.price}/month
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment Method */}
              <div className="mt-8">
                <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Payment method</h4>
                <div className="space-y-3">
                  {paymentProviders.length === 0 && (
                    <div className="text-gray-400 text-sm">No payment methods available.</div>
                  )}
                  {paymentProviders.map((provider) => (
                    <div
                      key={provider._id}
                      onClick={() => setPaymentMethod(provider.name.toLowerCase())}
                      className={`p-3 border rounded-lg cursor-pointer flex items-center gap-3 transition-colors ${
                        paymentMethod === provider.name.toLowerCase()
                          ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20"
                          : "border-gray-200 dark:border-gray-600 hover:border-indigo-300"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border-2 ${
                          paymentMethod === provider.name.toLowerCase()
                            ? "border-indigo-500 bg-indigo-500"
                            : "border-gray-300"
                        }`}
                      >
                        {paymentMethod === provider.name.toLowerCase() && (
                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                        )}
                      </div>
                      {/* Icon by provider */}
                      {provider.name === 'Stripe' && <FaCreditCard className="text-gray-600 dark:text-gray-300" />}
                      {provider.name === 'PayPal' && <FaPaypal className="text-blue-600" />}
                      {provider.name !== 'Stripe' && provider.name !== 'PayPal' && (
                        <span className="inline-block w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-xs font-bold text-gray-500 dark:text-gray-300 uppercase">{provider.name[0]}</span>
                      )}
                      <span className="text-gray-800 dark:text-gray-100 font-medium">{provider.name}</span>
                      <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{provider.supported_currencies?.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscribe Button or Stripe Payment */}
              {showStripe && stripeClientSecret ? (
                <Elements options={{ clientSecret: stripeClientSecret }} stripe={stripePromise}>
                  <div className="relative">
                    <StripePaymentForm
                      clientSecret={stripeClientSecret}
                      purchaseId={stripePurchaseId}
                      paymentIntentId={stripePaymentIntentId}
                      onSuccess={() => {
                        setShowStripe(false);
                        onClose && onClose();
                      }}
                      onError={err => setSubscribeError(err.message)}
                      onPaymentSuccess={typeof onPaymentSuccess === 'function' ? onPaymentSuccess : undefined}
                      onStripeReady={() => setStripeReady(true)}
                    />
                    {!stripeReady && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/80 z-20">
                        <svg className="animate-spin h-8 w-8 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        <div className="text-indigo-700 dark:text-indigo-300 font-semibold text-lg">Preparing payment...</div>
                      </div>
                    )}
                  </div>
                </Elements>
              ) : showPaymentLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  <div className="text-indigo-700 dark:text-indigo-300 font-semibold text-lg">Preparing payment...</div>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleSubscribe}
                    disabled={submitting || showStripe}
                    className="w-full mt-6 py-3.5 rounded-lg text-white font-semibold bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : `Subscribe to ${plans && plans[selectedPlan] ? plans[selectedPlan].name : ""}`}
                  </button>
                  {subscribeError && <div className="text-red-500 text-sm mt-2">{subscribeError}</div>}

                  {/* Or divider */}
                  <div className="relative my-6">
                    <div className="h-px bg-gray-200 dark:bg-gray-700"></div>
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-900 px-3 text-xs text-gray-500">or</span>
                  </div>

                  {/* Redeem Code CTA */}
                  {!showRedeem ? (
                    <button
                      type="button"
                      onClick={() => setShowRedeem(true)}
                      className="w-full py-3 rounded-lg font-medium transition-colors border bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2"
                    >
                      <FaGift className="text-indigo-500" /> Have a subscription code?
                    </button>
                  ) : (
                    <div className="mt-4 p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                        <FaGift className="text-indigo-600" /> Apply code
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={redeemCode}
                          onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                          placeholder="ENTER CODE"
                          className="flex-1 px-3 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 tracking-widest uppercase placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <button
                          type="button"
                          onClick={handleRedeem}
                          disabled={redeeming || !redeemCode}
                          className="px-5 py-3 rounded-lg font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                        >
                          {redeeming ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                      {redeemError && <div className="text-red-500 text-xs mt-2">{redeemError}</div>}
                      {redeemSuccess && <div className="text-emerald-600 dark:text-emerald-400 text-xs mt-2">{redeemSuccess}</div>}
                    </div>
                  )}
                </>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Cancel anytime. No hidden fees. 30-day money-back guarantee.
              </p>
              <div className="mt-3 flex items-center justify-center gap-4 text-[10px] text-gray-500 dark:text-gray-400">
                <div className="inline-flex items-center gap-2"><FaBolt className="text-indigo-500" /> Instant activation</div>
              </div>
            </div>

            {/* Right Column - Features */}
            <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Everything in Pro
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-1 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-xl bg-white/70 dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/60">
                    <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Security Badge */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mt-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    Secure Payment
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Your payment information is encrypted and secure. We never store your credit card details.
                </p>
              </div>

              {/* Testimonial */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-700">
                <p className="text-sm text-indigo-800 dark:text-indigo-200 italic mb-2">
                  "This platform has transformed how our engineering team interacts with AS3000 standards. We can now quickly find specific clauses and get contextual answers in seconds instead of hours!"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    M
                  </div>
                  <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                    Aadil Siddiqui., Senior Software Engineer
                  </span>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                <details className="rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/60 dark:bg-gray-800/40 p-4">
                  <summary className="cursor-pointer font-semibold text-gray-800 dark:text-gray-200">Can I cancel anytime?</summary>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Yes, you can cancel at any time from your account settings. Your plan remains active until the end of the current billing period.</p>
                </details>
                <details className="rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/60 dark:bg-gray-800/40 p-4">
                  <summary className="cursor-pointer font-semibold text-gray-800 dark:text-gray-200">Do you offer refunds?</summary>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">We offer a 30‑day money‑back guarantee if you’re not satisfied.</p>
                </details>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Scroll indicator arrow */}
        {showScrollIndicator && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-indigo-600 text-white rounded-full p-2 shadow-lg animate-bounce">
              <FaChevronDown className="w-4 h-4" />
            </div>
          </div>
        )}
      </div>
    </AnimatedModal>
  );
}
